import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CaptionProps, DayPicker, Matcher } from 'react-day-picker';
import { isSameDay } from 'date-fns';
import cx from 'classnames';
import CalendarCaption from 'lib/DatePicker/CalendarCaption';
import { getInputBorderStyle } from 'lib/BaseInput';
import { FIRST_DAY_OF_THE_WEEK } from 'consts/dateTimePicker';
import { DateRangePickerProps, RangeSelection } from './DateRangePicker.types';
import './DateRangePicker.pcss';

const DEFAULT_LENGTH_SELECTION = 6;

export const formatDate = (date?: Date, defaultLabel: string = 'Select'): string => {
  if (!date) return defaultLabel;

  const monthNumber = date.getMonth() + 1;

  return `${date.getDate()}/${monthNumber}/${date.getFullYear()}`;
};

const YEARS_BEFORE = 0;
const DEFAULT_YEARS_AFTER = 3;

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  from,
  to,
  time,
  setFrom,
  setTo,
  isFloatingRangeVisible = false,
  isTimePickerEnabled = false,
  isMinutesSelection = false,
  floatingFrom,
  floatingTo,
  setFloatingFrom,
  setFloatingTo,
  modifyStartDate,
  modifyEndDate,
  disabledDatePickerDays,
  isDisabled = false,
  width = 'w-[350px]',
  navigationWidth = 'w-[326px]',
  isWeekNumbersVisible = false,
  shouldDefaultToDateAfterFromSelection = false,
  paperWeekStartDay,
  yearsAfter = DEFAULT_YEARS_AFTER,
  defaultMonth,
  onMonthChange: onMonthChangeProp,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: DateRangePickerProps) => {
  const today = new Date();
  const [monthState, setMonthState] = useState<Date>(defaultMonth || new Date(from || today));

  const [editedDate, setEditedDate] = useState<RangeSelection>('FROM');

  const setMonth = useCallback(
    (newMonth: Date) => {
      setMonthState(newMonth);
      onMonthChangeProp?.(newMonth);
    },
    [onMonthChangeProp],
  );

  const handleFromChange = (date: Date): void => {
    if (modifyStartDate) {
      setFrom(modifyStartDate(date));
    } else {
      setFrom(date);
    }
  };

  const handleToChange = (date: Date): void => {
    if (modifyEndDate) setTo(modifyEndDate(date));
    else setTo(date);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleDayClick = (day: Date): void => {
    if (isDisabled) return;

    if (!from || !to) {
      handleFromChange(day);

      if (shouldDefaultToDateAfterFromSelection) {
        const futureDate = new Date(day);
        futureDate.setDate(day.getDate() + DEFAULT_LENGTH_SELECTION);
        handleToChange(futureDate);
      } else {
        handleToChange(day);
      }

      setEditedDate('TO');

      return;
    }

    if (isFloatingRangeVisible && (editedDate === 'FLOATING_FROM' || editedDate === 'FLOATING_TO')) {
      if ((floatingFrom && isSameDay(day, floatingFrom)) || (floatingTo && isSameDay(day, floatingTo))) {
        setFloatingFrom?.(undefined);
        setFloatingTo?.(undefined);
        return;
      }

      if (editedDate === 'FLOATING_FROM') {
        if (day > to) {
          setFloatingTo?.(day);
          if (!floatingFrom) setFloatingFrom?.(from);
          return;
        }

        setFloatingFrom?.(day);
        if (!floatingTo) setFloatingTo?.(to);
        setEditedDate('FLOATING_TO');
        return;
      }

      if (editedDate === 'FLOATING_TO') {
        if (day < from) {
          setFloatingFrom?.(day);
          if (!floatingTo) setFloatingTo?.(to);
          return;
        }

        setFloatingTo?.(day);
        if (!floatingFrom) setFloatingFrom?.(from);
        setEditedDate('FLOATING_FROM');
        return;
      }
    }

    if (day < from && !isSameDay(day, from)) {
      handleFromChange(day);
      return;
    }

    if (day > to && !isSameDay(day, to)) {
      handleToChange(day);
      return;
    }

    if (editedDate === 'FROM') {
      handleFromChange(day);
      setEditedDate('TO');
      return;
    }

    if (editedDate === 'TO') {
      handleToChange(day);
      setEditedDate(isFloatingRangeVisible ? 'FLOATING_FROM' : 'FROM');
    }
  };

  const handleStartDateTimeChange = (dateTime: string): void => {
    if (!from) return;

    const [hours, minutes] = dateTime.split(':');

    const updated = new Date(from.getTime());
    updated.setHours(+hours, isMinutesSelection ? +minutes : 0, 0, 0);
    setFrom(updated);
  };

  const handleYearMonthChange = useCallback((newDate: Date): void => setMonth(newDate), [setMonth]);

  const rangeCaptionPropsRef = useRef({ yearsAfter, navigationWidth });
  rangeCaptionPropsRef.current = { yearsAfter, navigationWidth };

  const rangeCaptionComponents = useMemo(() => {
    const stableRangeCaption = (props: CaptionProps): React.ReactElement => (
      <CalendarCaption
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        onMonthChange={handleYearMonthChange}
        yearsBefore={YEARS_BEFORE}
        yearsAfter={rangeCaptionPropsRef.current.yearsAfter}
        navigationWidth={rangeCaptionPropsRef.current.navigationWidth}
      />
    );
    stableRangeCaption.displayName = 'StableRangeCaption';
    return { Caption: stableRangeCaption };
  }, [handleYearMonthChange]);

  const isSingleDayRange = from && to && isSameDay(from, to);

  const modifiers: Record<string, Matcher | Matcher[]> = {
    fixedRange: from && to ? { from, to } : false,
    floatingRange: floatingFrom && floatingTo ? { from: floatingFrom, to: floatingTo } : false,
    fixedRangeStart: from || false,
    fixedRangeEnd: to || false,
    floatingRangeStart: floatingFrom || false,
    floatingRangeEnd: floatingTo || false,
    rangeOverlapFrom: floatingFrom && from && floatingFrom < from ? from : false,
    rangeOverlapEnd: floatingTo && to && floatingTo > to ? to : false,
    paperWeekStart: paperWeekStartDay ? { dayOfWeek: [paperWeekStartDay] } : false,
    singleDayRange: isSingleDayRange ? from : false,
  };

  const disabledDays: Matcher[] = useMemo(() => {
    if (disabledDatePickerDays) return disabledDatePickerDays;

    return [
      {
        before: new Date(),
      },
      ...(editedDate === 'FLOATING_TO' && floatingFrom
        ? [
            {
              before: floatingFrom,
            },
          ]
        : []),
      ...(editedDate === 'FLOATING_FROM' || editedDate === 'FLOATING_TO'
        ? [
            ...(from && to
              ? [
                  {
                    from,
                    to,
                  },
                ]
              : []),
          ]
        : []),
    ];
  }, [editedDate, from, to, floatingFrom, disabledDatePickerDays]);

  useEffect(() => {
    if (!isFloatingRangeVisible) return;

    if (floatingFrom && from && floatingFrom > from) {
      setFloatingFrom?.(from);
    }

    if (floatingTo && to && floatingTo < to) {
      setFloatingTo?.(to);
    }
  }, [from, to, floatingFrom, floatingTo, isFloatingRangeVisible, setFloatingFrom, setFloatingTo]);

  useEffect(() => {
    setEditedDate(isFloatingRangeVisible ? 'FLOATING_FROM' : 'FROM');

    return () => {
      setEditedDate('FLOATING_FROM');
    };
  }, [isFloatingRangeVisible]);

  const renderDateButton = (
    date: string,
    label: string,
    rangeSelection: RangeSelection,
    dataTestId: string,
    disabled: boolean,
  ): JSX.Element => (
    <button
      className={cx('flex flex-col w-1/2 border border-neutral-300 p-2.5 bg-white text-left', {
        'ring-1 border-primary-600 ring-primary-600 bg-primary-50 z-10': !disabled && editedDate === rangeSelection,
        'rounded-l-md': rangeSelection === 'FROM' || rangeSelection === 'FLOATING_FROM',
        'rounded-r-md': rangeSelection === 'TO' || rangeSelection === 'FLOATING_TO',
        'cursor-not-allowed opacity-60': disabled,
      })}
      data-test-id={dataTestId}
      onClick={() => setEditedDate(rangeSelection)}
      type="button"
      disabled={disabled}
    >
      <span className="text-essential-primary leading-5">{label}</span>
      <span className="text-essential-tertiary leading-5">{date}</span>
    </button>
  );

  const selectedDays: Matcher[] = [];
  if (from && to) selectedDays.push({ from, to });
  if (floatingFrom && floatingTo) selectedDays.push({ from: floatingFrom, to: floatingTo });

  return (
    <div
      className="DateTimePicker DateTimePicker-range flex flex-col items-center"
      data-test-id="date-range-picker-calendar"
    >
      <div className="flex flex-row items-center w-full px-3">
        <div className="h-9 w-1 bg-primary-600 rounded-sm mr-2" />
        {renderDateButton(
          from ? formatDate(from) : 'Select date',
          'Start date',
          'FROM',
          'date-start',
          isDisabled || !to,
        )}
        {renderDateButton(to ? formatDate(to) : 'Select date', 'End date', 'TO', 'date-end', isDisabled || !from)}
      </div>
      {isFloatingRangeVisible && (
        <div className="flex flex-row items-center w-full px-3 mt-4 mb-1">
          <div className="h-9 w-1 bg-blue-500-opacity-30 rounded-sm mr-2" />
          {renderDateButton(formatDate(floatingFrom, 'Earliest'), '', 'FLOATING_FROM', 'floating-date-start', false)}
          {renderDateButton(formatDate(floatingTo, 'Latest'), '', 'FLOATING_TO', 'floating-date-end', false)}
        </div>
      )}
      <div className={cx(width, { 'DateTimePicker-range--calendar-disabled': isDisabled })}>
        <DayPicker
          showWeekNumber={isWeekNumbersVisible}
          ISOWeek={isWeekNumbersVisible}
          modifiers={modifiers}
          modifiersClassNames={{
            fixedRange: 'rdp-day_fixedRange',
            fixedRangeStart: 'rdp-day_fixedRangeStart',
            fixedRangeEnd: 'rdp-day_fixedRangeEnd',
            floatingRange: 'rdp-day_floatingRange',
            floatingRangeStart: 'rdp-day_floatingRangeStart',
            floatingRangeEnd: 'rdp-day_floatingRangeEnd',
            rangeOverlapFrom: 'rdp-day_rangeOverlapFrom',
            rangeOverlapEnd: 'rdp-day_rangeOverlapEnd',
            paperWeekStart: 'rdp-day_paperWeekStart',
            singleDayRange: 'rdp-day_singleDayRange',
          }}
          modifiersStyles={{
            paperWeekStart: {
              backgroundColor: '#2C24CC33',
            },
            fixedRange: { borderRadius: 0 },
            fixedRangeStart: { borderRadius: '12px 0 0 12px' },
            fixedRangeEnd: { borderRadius: '0 12px 12px 0' },
            floatingRange: { borderRadius: 0 },
            floatingRangeStart: { borderRadius: '12px 0 0 12px' },
            floatingRangeEnd: { borderRadius: '0 12px 12px 0' },
            singleDayRange: { borderRadius: '12px' },
          }}
          showOutsideDays
          weekStartsOn={FIRST_DAY_OF_THE_WEEK}
          selected={selectedDays}
          disabled={disabledDays}
          month={monthState}
          onMonthChange={setMonth}
          onDayClick={handleDayClick}
          components={rangeCaptionComponents}
        />
        {isTimePickerEnabled ? (
          <div className="flex justify-center my-4">
            <input
              className={cx(
                getInputBorderStyle({ isDisabled }),
                'text-sm text-center p-1 outline-none bg-neutral-50 w-min',
              )}
              type="time"
              data-test-id="date-range-picker-time-input"
              onChange={(e) => handleStartDateTimeChange(e.target.value)}
              value={time}
              disabled={isDisabled}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DateRangePicker;
